/**
 * Pure projection helpers — translate the SDK stream + locally-tracked
 * metadata into the display-block shape the panel renders. No Angular
 * or SDK imports so the chat service can compose inside `computed()`
 * and tests can call directly.
 */

import type {
  ChatBlock,
  ChatMessage,
  MessageMeta,
  SdkMessageLike,
  SdkToolCallLike,
  StreamHandleLike,
  ThoughtStep,
  ToolCall,
  ToolCallState
} from '@portal/app/shared/components/ai-assistant-panel/types/chat-block.types';
import { contentToText } from '@portal/app/shared/components/ai-assistant-panel/helpers/chat-content.helpers';

export function buildMessages(
  stream: StreamHandleLike,
  meta: ReadonlyMap<string, MessageMeta>
): ChatMessage[] {
  const sdkMessages = stream.messages();
  const isLoading = stream.isLoading();

  // While streaming, drop the most recent AI message — the live
  // block owns its rendering. Without this, the same content
  // double-renders against the in-flight bubble.
  let inFlightIndex = -1;
  if (isLoading) {
    for (let i = sdkMessages.length - 1; i >= 0; i--) {
      if (sdkMessages[i]?.type === 'ai') {
        inFlightIndex = i;
        break;
      }
    }
  }

  const result: ChatMessage[] = [];

  for (let i = 0; i < sdkMessages.length; i++) {
    if (i === inFlightIndex) continue;
    const m = sdkMessages[i];
    if (!m || m.type === 'tool') continue;

    const id = m.id ?? `pending-${i}`;
    const role = m.type === 'human' ? 'user' : 'assistant';
    const captured = m.id ? meta.get(m.id) : undefined;

    let toolCalls: ToolCall[] | undefined;
    if (m.type === 'ai') {
      const calls = stream.getToolCalls(m);
      if (calls.length > 0) toolCalls = projectToolCalls(calls);
    }

    result.push({
      id,
      role,
      content: contentToText(m.content),
      timestamp: 0,
      lsRunId: captured?.lsRunId,
      toolCalls
    });
  }

  markFinalOfTurn(result, isLoading);
  return result;
}

/**
 * Tag the last text-bearing assistant message of each turn as
 * `isFinalOfTurn`. Boundaries are user messages or end-of-history;
 * the current turn stays unmarked while loading.
 */
export function markFinalOfTurn(
  messages: ChatMessage[],
  isLoading: boolean
): void {
  let candidateIdx = -1;

  const commit = (idx: number): void => {
    const target = idx >= 0 ? messages[idx] : undefined;
    if (!target) return;
    messages[idx] = { ...target, isFinalOfTurn: true };
  };

  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (!m) continue;
    if (m.role === 'user') {
      commit(candidateIdx);
      candidateIdx = -1;
      continue;
    }
    if (m.role === 'assistant' && m.content) {
      candidateIdx = i;
    }
  }

  if (!isLoading) commit(candidateIdx);
}

export function projectToolCalls(
  calls: readonly SdkToolCallLike[]
): ToolCall[] {
  return calls.map((tc) => ({
    id: tc.id,
    name: tc.call?.name ?? 'tool',
    args: tc.call?.args,
    result: tc.result?.content,
    state: normalizeToolCallState(tc.state)
  }));
}

function normalizeToolCallState(state: string | undefined): ToolCallState {
  if (state === 'completed' || state === 'error' || state === 'pending') {
    return state;
  }
  return 'pending';
}

/**
 * Walk the message stream turn-by-turn and emit display blocks.
 * Per assistant turn, tool calls + intermediate reasoning fold into a
 * single `thought` block; the final text-bearing message becomes the
 * `response` block. Consecutive duplicate text dedupes.
 */
export function buildDisplayBlocks(messages: ChatMessage[]): ChatBlock[] {
  const blocks: ChatBlock[] = [];
  let i = 0;

  while (i < messages.length) {
    const msg = messages[i];
    if (!msg) {
      i++;
      continue;
    }

    if (msg.role === 'user') {
      blocks.push({ kind: 'user', id: msg.id, content: msg.content });
      i++;
      continue;
    }
    if (msg.role === 'error') {
      blocks.push({ kind: 'error', id: msg.id, content: msg.content });
      i++;
      continue;
    }

    const turnAnchorId = msg.id;
    const turnSteps: ThoughtStep[] = [];
    let pendingTools: ToolCall[] = [];
    let lastText = '';
    let response: ChatMessage | null = null;

    const flushPendingTools = (): void => {
      if (pendingTools.length === 0) return;
      const head = pendingTools[0];
      if (!head) {
        pendingTools = [];
        return;
      }
      turnSteps.push({
        kind: 'tools',
        id: `tools-${head.id}`,
        tools: pendingTools.slice()
      });
      pendingTools = [];
    };

    while (i < messages.length && messages[i]?.role === 'assistant') {
      const m = messages[i];
      if (!m) {
        i++;
        continue;
      }

      if (m.toolCalls && m.toolCalls.length > 0) {
        pendingTools.push(...m.toolCalls);
      }

      if (m.content && m.content !== lastText) {
        flushPendingTools();
        if (m.isFinalOfTurn) {
          response = m;
        } else {
          turnSteps.push({
            kind: 'reasoning',
            id: m.id,
            content: m.content
          });
        }
        lastText = m.content;
      }
      i++;
    }
    flushPendingTools();

    if (turnSteps.length > 0) {
      blocks.push({
        kind: 'thought',
        id: `thought-${turnAnchorId}`,
        steps: turnSteps
      });
    }

    if (response) {
      blocks.push({
        kind: 'response',
        id: response.id,
        content: response.content,
        lsRunId: response.lsRunId
      });
    }
  }

  return blocks;
}

/** Tool calls on the latest AI message — drives the live block-thought. */
export function liveToolCallsFor(stream: StreamHandleLike): ToolCall[] {
  const msgs = stream.messages();
  let lastAi: SdkMessageLike | undefined;
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i];
    if (m && m.type === 'ai') {
      lastAi = m;
      break;
    }
  }
  if (!lastAi) return [];
  return projectToolCalls(stream.getToolCalls(lastAi));
}

/** Pick which SDK message turn-finalize metadata lands on. */
export function findFinalizeTarget(
  msgs: readonly SdkMessageLike[]
): SdkMessageLike | undefined {
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i];
    if (m && m.type === 'ai' && m.id && contentToText(m.content)) {
      return m;
    }
  }
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i];
    if (m && m.type === 'ai' && m.id) {
      return m;
    }
  }
  return undefined;
}
